import { PageErrorBoundary } from '@/components/PageErrorBoundary';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useThemeStore } from '@/store/themeStore';
import { cn } from '@/utils/cn';
import { Suspense, useEffect, useId } from 'react';
import { Outlet, useLocation } from 'react-router';

export function AppLayout() {
  const { sidebarCollapsed, setSidebarCollapsed, mobileNavOpen, setMobileNavOpen } = useThemeStore();
  const location = useLocation();
  const errorTitleId = useId();
  const errorDescriptionId = useId();

  // Auto-collapse sidebar on small viewports
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) setSidebarCollapsed(true);
    };
    handleChange(mq);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [setSidebarCollapsed]);

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-hidden bg-background print:block print:h-auto print:overflow-visible">
        <Sidebar />
        {/* Backdrop for the mobile drawer (below lg only) */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            aria-hidden="true"
            onClick={() => setMobileNavOpen(false)}
          />
        )}
        <div
          className={cn(
            'flex flex-1 flex-col overflow-hidden transition-all duration-300',
            // Sidebar is an overlay drawer below lg, so no content offset there.
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
            'print:ml-0 print:block print:overflow-visible'
          )}
        >
          <Header />
          <main
            className="flex-1 overflow-auto p-6 print:overflow-visible print:p-0"
            style={{
              background: 'hsl(var(--background))',
            }}
          >
            <PageErrorBoundary
              descriptionId={errorDescriptionId}
              resetKey={location.key}
              titleId={errorTitleId}
            >
              <Suspense fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
              }>
                <Outlet />
              </Suspense>
            </PageErrorBoundary>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
